/**
 * Game Analytics Service
 * 
 * ARCHITECTURE:
 * - Create event at GAME START, update on completion
 * - NO client-side aggregate updates (server-only via Cloud Functions)
 * - Reads from pre-aggregated game.stats and gameDailyStats
 * 
 * This replaces:
 * - createCampaignEvent / updateCampaignEvent in Database.jsx
 * - statsService.js (partially)
 * - Client-side increment() calls
 */

import { collection, doc, setDoc, getDoc, getDocs, query, where, orderBy, limit, serverTimestamp, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';

// ============================================
// CONSTANTS
// ============================================

const COLLECTIONS = {
  GAMES: 'campaign',
  GAME_EVENTS: 'gameEvent',
  GAME_DAILY_STATS: 'gameDailyStats',
  GAME_STATS_SHARDS: 'gameStatsShards'
};

// Cache configuration
const STATS_CACHE_KEY = 'game_stats_cache_v1';
export const getStatsCacheDuration = (totalPlays = 0) => {
  if (totalPlays === 0) return 5 * 1000; // very short for empty campaigns
  if (totalPlays < 10) return 0; // no cache to show instant updates under 10 plays
  if (totalPlays < 50) return 30 * 1000;
  if (totalPlays < 200) return 60 * 1000;
  return 2 * 60 * 1000; // 2 minutes max
};

// In-memory cache
const memoryCache = new Map();
// In-flight request dedup. If two callers ask for the same cacheKey before
// the first Firestore round-trip completes, they share the same promise
// rather than both firing parallel reads. Critical on Dashboard mount,
// where fetchGameStats + fetchMultipleGameDailyStats both want the same
// daily-stats window and would otherwise double-read.
const inFlight = new Map();

// ============================================
// HELPERS
// ============================================

/**
 * Generate a simple session ID (no UUID library needed)
 */
const generateSessionId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

const PLAYER_ID_KEY = 'sf_player_id';
const getPlayerId = () => {
  try {
    let id = localStorage.getItem(PLAYER_ID_KEY);
    if (!id) {
      id = generateSessionId();
      localStorage.setItem(PLAYER_ID_KEY, id);
    }
    return id;
  } catch {
    return generateSessionId(); // fallback if localStorage unavailable
  }
};

/**
 * Get device type from user agent
 */
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/iPad|Android(?!.*Mobile)/i.test(ua)) return 'tablet';
  if (/iPhone|iPod|Android/i.test(ua)) return 'mobile';
  return 'desktop';
};

/**
 * Extract host from referrer URL
 */
const getReferrerHost = () => {
  try {
    if (!document.referrer) return null;
    const url = new URL(document.referrer);
    return url.hostname.replace('www.', '');
  } catch {
    return null;
  }
};

/**
 * Get UTM parameters from URL
 */
const getUtmParams = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get('utm_source') || null,
      utmCampaign: params.get('utm_campaign') || null
    };
  } catch {
    return { utmSource: null, utmCampaign: null };
  }
};

/**
 * Get today's date key (YYYY-MM-DD)
 */
const getDateKey = () => new Date().toISOString().split('T')[0];

// ============================================
// EVENT RECORDING (Single write at game end)
// ============================================

/**
 * Record a game event - called ONCE at game end
 * 
 * This is the ONLY write clients make to analytics.
 * Aggregation happens server-side via Cloud Functions.
 * 
 * @param {Object} params
 * @param {string} params.gameId - The game ID
 * @param {Date} params.startedAt - When the game started
 * @param {number} params.score - Final score (null if N/A)
 * @param {boolean} params.completed - Did player complete the game?
 * @returns {string} The event document ID
 */
export const recordGameStart = async ({
  gameId,
  startedAt
}) => {
  if (!gameId) throw new Error('gameId is required');
  
  const utmParams = getUtmParams();
  
  const eventData = {
    gameId,
    sessionId: generateSessionId(),
    playerId: getPlayerId(),
    startedAt: startedAt || null,
    endedAt: null,
    durationSeconds: 0,
    score: null,
    completed: false,
    // These are set to false initially; updated via updateGameEventAction
    shared: false,
    ctaClicked: false,
    ctaActionType: null,
    leaderboardJoined: false,
    // Device/environment (captured once)
    deviceType: getDeviceType(),
    language: navigator.language || 'unknown',
    referrerHost: getReferrerHost(),
    utmSource: utmParams.utmSource,
    utmCampaign: utmParams.utmCampaign,
    // Server timestamp for ordering/aggregation
    createdAt: serverTimestamp(),
    dateKey: getDateKey()
  };
  
  // Write single event document
  const eventRef = doc(collection(db, COLLECTIONS.GAME_EVENTS));
  await setDoc(eventRef, eventData);
  
  console.debug('📊 Game event started:', eventRef.id);
  return eventRef.id;
};

/**
 * Record a page view — called once when the Play page mounts.
 *
 * This is a lightweight fire-and-forget write used to measure QR-scan-to-play
 * conversion (how many people loaded the page vs actually tapped Play).
 * No auth required.
 *
 * @param {string} campaignId
 */
export const recordPageView = async (campaignId) => {
  if (!campaignId) return;

  try {
    const viewRef = doc(collection(db, COLLECTIONS.GAMES, campaignId, 'pageViews'));
    await setDoc(viewRef, {
      timestamp: serverTimestamp(),
      deviceType: getDeviceType(),
      referrerHost: getReferrerHost(),
      utmSource: getUtmParams().utmSource || null,
    });
    console.debug('📊 Page view recorded for campaign:', campaignId);
  } catch (err) {
    // Non-critical — swallow errors so it never blocks game loading
    console.debug('Page view recording failed:', err);
  }
};

/**
 * Count the total page views for a campaign. Used to surface the
 * "scan/click → play" conversion rate on the campaign dashboard.
 * Uses getCountFromServer so cost is 1 read regardless of view volume.
 */
export const getPageViewCount = async (campaignId) => {
  if (!campaignId) return 0;
  try {
    const ref = collection(db, COLLECTIONS.GAMES, campaignId, 'pageViews');
    const snap = await getCountFromServer(ref);
    return snap.data().count;
  } catch (err) {
    console.debug('Page view count failed:', err);
    return 0;
  }
};

/**
 * Complete a game event - called at game end
 *
 * @param {string} eventId
 * @param {Object} params
 * @param {Date} params.startedAt
 * @param {number} params.score
 */
export const recordGameComplete = async ({
  eventId,
  startedAt,
  score = null
}) => {
  if (!eventId) throw new Error('eventId is required');

  const endedAt = new Date();
  const durationSeconds = startedAt
    ? Math.round((endedAt - startedAt) / 1000)
    : 0;

  const eventRef = doc(db, COLLECTIONS.GAME_EVENTS, eventId);
  await setDoc(eventRef, {
    endedAt,
    durationSeconds,
    score,
    completed: true
  }, { merge: true });

  console.debug('📊 Game event completed:', eventId);
};

// Backwards compatibility: keep old API as start+complete in one call
export const recordGameEvent = async (params) => {
  const eventId = await recordGameStart(params);
  await recordGameComplete({ eventId, startedAt: params.startedAt, score: params.score });
  return eventId;
};

/**
 * Update a game event with post-game actions (share, CTA, leaderboard)
 * 
 * This is called from EndCard when player takes an action.
 * Still a single document update, no aggregate updates.
 * 
 * @param {string} eventId - The event document ID from recordGameEvent
 * @param {Object} updates - Fields to update
 */
export const updateGameEventAction = async (eventId, updates) => {
  if (!eventId) {
    console.warn('No eventId provided for action update');
    return;
  }
  
  const allowedFields = ['shared', 'ctaClicked', 'ctaActionType'];
  const sanitizedUpdates = {};
  
  for (const field of allowedFields) {
    if (updates[field] !== undefined) {
      sanitizedUpdates[field] = updates[field];
    }
  }
  
  if (Object.keys(sanitizedUpdates).length === 0) return;
  
  const eventRef = doc(db, COLLECTIONS.GAME_EVENTS, eventId);
  await setDoc(eventRef, sanitizedUpdates, { merge: true });
  
  console.debug('📊 Game event action updated:', sanitizedUpdates);
};

// ============================================
// STATS RETRIEVAL (Read-only, from pre-aggregated data)
// ============================================

/**
 * Get cached stats from localStorage
 */
const getLocalCache = (gameId) => {
  try {
    const cached = localStorage.getItem(`${STATS_CACHE_KEY}_${gameId}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      const totalPlays = parsed?.data?.totalPlays || 0;
      const cacheDuration = getStatsCacheDuration(totalPlays);
      if (cacheDuration > 0 && Date.now() - parsed.timestamp < cacheDuration) {
        return parsed.data;
      }
    }
  } catch (e) {
    console.debug('Cache read error:', e);
  }
  return null;
};

/**
 * Set cached stats to localStorage
 */
const setLocalCache = (gameId, data) => {
  try {
    localStorage.setItem(`${STATS_CACHE_KEY}_${gameId}`, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.debug('Cache write error:', e);
  }
};

/**
 * Fetch game stats - reads ONLY from pre-aggregated game.stats
 * 
 * This does NOT query raw events. Aggregates are computed server-side.
 * 
 * @param {string} gameId
 * @returns {Object} Stats object
 */
export const fetchGameStats = async (gameId, { forceRefresh } = {}) => {
  if (forceRefresh) {
    invalidateGameStats(gameId);
  }

  // Check memory cache
  const memoryCached = memoryCache.get(gameId);
  if (memoryCached) {
    const cacheDuration = getStatsCacheDuration(memoryCached?.data?.totalPlays || 0);
    if (cacheDuration > 0 && Date.now() - memoryCached.timestamp < cacheDuration) {
      return memoryCached.data;
    }
  }

  // Check localStorage cache
  const localCached = getLocalCache(gameId);
  if (localCached) {
    memoryCache.set(gameId, { data: localCached, timestamp: Date.now() });
    return localCached;
  }
  
  // Guard: undefined/empty gameId crashes Firestore path construction
  if (!gameId) return getEmptyStats();

  // Fetch from game.stats (single doc read)
  try {
    const gameDoc = await getDoc(doc(db, COLLECTIONS.GAMES, gameId));
    
    if (!gameDoc.exists()) {
      return getEmptyStats();
    }
    
    const gameData = gameDoc.data();
    const stats = gameData.stats || getEmptyStats();
    let totalPlays = stats.totalPlays || 0;

    // Merge with gameDailyStats for fresher numbers (triggers update gameDailyStats instantly,
    // while campaign.stats only syncs every 5 min). Uses cached daily data — no extra reads.
    let totalCompletions = stats.totalCompletions || 0;
    let totalShares = stats.totalShares || 0;
    let totalCtas = stats.totalCtas || 0;
    let totalLeaderboardJoins = stats.totalLeaderboardJoins || 0;
    let totalEmailCaptures = stats.totalEmailCaptures || 0;
    let totalDurationSeconds = stats.totalDurationSeconds || 0;
    let devices = stats.devices || { mobile: 0, desktop: 0, tablet: 0 };
    try {
      // Use the same 30-day window as Dashboard's separate
      // fetchMultipleGameDailyStats call so the cache key matches and we
      // only hit Firestore once per campaign per mount instead of twice.
      const daily = await fetchGameDailyStats(gameId, 30);
      const sums = daily.reduce((acc, d) => ({
        plays: acc.plays + (d.plays || 0),
        completions: acc.completions + (d.completions || 0),
        shares: acc.shares + (d.shares || 0),
        ctas: acc.ctas + (d.ctas || 0),
        leaderboardJoins: acc.leaderboardJoins + (d.leaderboardJoins || 0),
        emailCaptures: acc.emailCaptures + (d.emailCaptures || 0),
        totalDurationSeconds: acc.totalDurationSeconds + (d.totalDurationSeconds || 0),
        mobile: acc.mobile + (d.devices?.mobile || 0),
        desktop: acc.desktop + (d.devices?.desktop || 0),
        tablet: acc.tablet + (d.devices?.tablet || 0),
      }), { plays: 0, completions: 0, shares: 0, ctas: 0, leaderboardJoins: 0, emailCaptures: 0, totalDurationSeconds: 0, mobile: 0, desktop: 0, tablet: 0 });
      // Use whichever source is higher (daily is fresher, campaign.stats covers full history)
      if (sums.plays > totalPlays) totalPlays = sums.plays;
      if (sums.completions > totalCompletions) {
        totalCompletions = sums.completions;
        totalDurationSeconds = sums.totalDurationSeconds; // keep duration in sync with completions source
      }
      if (sums.shares > totalShares) totalShares = sums.shares;
      if (sums.ctas > totalCtas) totalCtas = sums.ctas;
      if (sums.leaderboardJoins > totalLeaderboardJoins) totalLeaderboardJoins = sums.leaderboardJoins;
      if (sums.emailCaptures > totalEmailCaptures) totalEmailCaptures = sums.emailCaptures;
      const dailyDeviceTotal = sums.mobile + sums.desktop + sums.tablet;
      const statsDeviceTotal = (devices.mobile || 0) + (devices.desktop || 0) + (devices.tablet || 0);
      if (dailyDeviceTotal > statsDeviceTotal) {
        devices = { mobile: sums.mobile, desktop: sums.desktop, tablet: sums.tablet };
      }
    } catch (_) {}

    // Ensure all fields exist
    const normalizedStats = {
      totalPlays,
      totalCompletions,
      totalShares,
      totalCtas,
      totalLeaderboardJoins,
      totalEmailCaptures,
      avgDurationSeconds: totalCompletions > 0
        ? Math.round(totalDurationSeconds / totalCompletions)
        : 0,
      completionRate: totalPlays > 0
        ? Math.round((totalCompletions / totalPlays) * 100)
        : 0,
      shareRate: totalCompletions > 0
        ? Math.round((totalShares / totalCompletions) * 100)
        : 0,
      ctaRate: totalCompletions > 0
        ? Math.round((totalCtas / totalCompletions) * 100)
        : 0,
      devices,
      lastUpdatedAt: stats.lastUpdatedAt || null,
      source: 'pre-aggregated'
    };

    // Cache
    memoryCache.set(gameId, { data: normalizedStats, timestamp: Date.now() });
    setLocalCache(gameId, normalizedStats);

    return normalizedStats;
  } catch (error) {
    console.error('Error fetching game stats:', error);
    return getEmptyStats();
  }
};

/**
 * Fetch daily stats for a game (for charts)
 * 
 * @param {string} gameId
 * @param {number} days - Number of days to fetch (default 7)
 * @returns {Array} Daily stats array
 */
export const fetchGameDailyStats = async (gameId, days = 7, { forceRefresh } = {}) => {
  // Guard: undefined/empty gameId crashes Firestore path construction
  if (!gameId) return [];

  const cacheKey = `daily_${gameId}_${days}`;

  if (forceRefresh) {
    memoryCache.delete(cacheKey);
  }

  // Check memory cache
  const memoryCached = memoryCache.get(cacheKey);
  if (memoryCached) {
    const totalPlays = memoryCached.totalPlays ?? memoryCached?.data?.reduce((sum, d) => sum + (d.plays || 0), 0) ?? 0;
    const cacheDuration = getStatsCacheDuration(totalPlays);
    if (cacheDuration > 0 && Date.now() - memoryCached.timestamp < cacheDuration) {
      return memoryCached.data;
    }
  }

  // Dedupe concurrent requests for the same key — second caller awaits
  // the first's in-flight promise instead of firing a parallel Firestore
  // query. Crucial when Dashboard mounts and several call sites all ask
  // for the same campaign's 30-day stats at once.
  const existing = inFlight.get(cacheKey);
  if (existing) return existing;

  const fetchPromise = (async () => {
    try {
    // Calculate date range
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const startDateKey = startDate.toISOString().split('T')[0];
    
    // Query gameDailyStats
    const dailyQuery = query(
      collection(db, COLLECTIONS.GAME_DAILY_STATS),
      where('gameId', '==', gameId),
      where('date', '>=', startDateKey),
      orderBy('date', 'desc'),
      limit(days)
    );
    
    const snapshot = await getDocs(dailyQuery);
    const toHourlyArray = (value) => {
      if (Array.isArray(value)) return value;
      const hourly = new Array(24).fill(0);
      if (value && typeof value === 'object') {
        for (let i = 0; i < 24; i++) {
          const key = String(i);
          hourly[i] = value[key] ?? value[i] ?? 0;
        }
      }
      return hourly;
    };
    // Backend used to write literal keys "hourlyPlays.0" etc. (set+merge); read those if hourlyPlays is missing
    const getHourlyFromDoc = (data, field) => {
      const nested = data[field];
      if (nested !== undefined && nested !== null) return toHourlyArray(nested);
      const hourly = new Array(24).fill(0);
      for (let i = 0; i < 24; i++) {
        const v = data[`${field}.${i}`];
        if (typeof v === 'number') hourly[i] = v;
      }
      return hourly;
    };

    const dailyStats = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        date: data.date,
        plays: data.plays || 0,
        completions: data.completions || 0,
        shares: data.shares || 0,
        ctas: data.ctas || 0,
        leaderboardJoins: data.leaderboardJoins || 0,
        completionRate: data.completionRate || 0,
        shareRate: data.shareRate || 0,
        ctaRate: data.ctaRate || 0,
        hourlyPlays: getHourlyFromDoc(data, 'hourlyPlays'),
        hourlyCtas: getHourlyFromDoc(data, 'hourlyCtas'),
        hourlyShares: getHourlyFromDoc(data, 'hourlyShares'),
        hourlyLeads: getHourlyFromDoc(data, 'hourlyLeads'),
        emailCaptures: data.emailCaptures || 0,
        hourlyEmailCaptures: getHourlyFromDoc(data, 'hourlyEmailCaptures'),
        uniquePlayers: data.uniquePlayers || 0,
        totalDurationSeconds: data.totalDurationSeconds || 0,
        devices: data.devices || {
          mobile: data['devices.mobile'] || 0,
          desktop: data['devices.desktop'] || 0,
          tablet: data['devices.tablet'] || 0,
        },
        topReferrers: (() => {
          // Prefer topReferrers array from daily rollup
          const rollup = (data.topReferrers || []).map(r => typeof r === 'object' && r !== null ? (r.key || String(r)) : r).filter(Boolean);
          if (rollup.length > 0) return rollup;
          // Fallback: convert real-time referrerCounts map to sorted array
          const counts = data.referrerCounts;
          if (counts && typeof counts === 'object') {
            return Object.entries(counts)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 20)
              .map(([key]) => key);
          }
          return [];
        })(),
        topUtmSources: (() => {
          const rollup = (data.topUtmSources || []).map(u => typeof u === 'object' && u !== null ? (u.key || String(u)) : u).filter(Boolean);
          if (rollup.length > 0) return rollup;
          const counts = data.utmSourceCounts;
          if (counts && typeof counts === 'object') {
            return Object.entries(counts)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 20)
              .map(([key]) => key);
          }
          return [];
        })()
      };
    }).reverse();
    
    // Cache
    const totalPlays = dailyStats.reduce((sum, d) => sum + (d.plays || 0), 0);
    memoryCache.set(cacheKey, { data: dailyStats, timestamp: Date.now(), totalPlays });

    return dailyStats;
    } catch (error) {
      console.error('Error fetching daily stats:', error);
      return [];
    }
  })();

  inFlight.set(cacheKey, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    inFlight.delete(cacheKey);
  }
};

/**
 * Get today's plays (and shares/ctas) for a single campaign.
 * Uses gameDailyStats and the current UTC date (matches backend chart and gameDailyStats doc IDs).
 *
 * @param {string} gameId
 * @returns {Promise<{ plays: number, shares: number, ctas: number }>}
 */
export const getTodayStatsForCampaign = async (gameId) => {
  const todayKey = new Date().toISOString().split('T')[0];
  const daily = await fetchGameDailyStats(gameId, 2);
  const today = daily.find((d) => d.date === todayKey);
  return {
    plays: today?.plays ?? 0,
    shares: today?.shares ?? 0,
    ctas: today?.ctas ?? 0,
    leaderboardJoins: today?.leaderboardJoins ?? 0
  };
};

/**
 * Get today's stats for multiple campaigns (for aggregation).
 *
 * @param {string[]} gameIds
 * @returns {Promise<Object>} Map of gameId -> { plays, shares, ctas }
 */
export const getTodayStatsForMultipleCampaigns = async (gameIds) => {
  if (!gameIds?.length) return {};
  const results = await Promise.all(
    gameIds.map((id) => getTodayStatsForCampaign(id).then((today) => ({ id, today })))
  );
  return results.reduce((acc, { id, today }) => {
    acc[id] = today;
    return acc;
  }, {});
};

/**
 * Fetch stats for multiple games
 * 
 * @param {string[]} gameIds
 * @returns {Object} Map of gameId -> stats
 */
export const fetchMultipleGameStats = async (gameIds) => {
  const results = await Promise.all(
    gameIds.map(id => fetchGameStats(id).then(stats => ({ id, stats })))
  );

  return results.reduce((acc, { id, stats }) => {
    acc[id] = stats;
    return acc;
  }, {});
};

/**
 * Fetch the last `days` of daily stats for many campaigns in parallel.
 * Returns { [campaignId]: dailyStatsArray }. Each daily stats array has
 * { date, plays, completions, ctas, emailCaptures, ... } per day.
 */
export const fetchMultipleGameDailyStats = async (gameIds, days = 7) => {
  const results = await Promise.all(
    gameIds.map(id => fetchGameDailyStats(id, days).then(daily => ({ id, daily })))
  );
  return results.reduce((acc, { id, daily }) => {
    acc[id] = Array.isArray(daily) ? daily : [];
    return acc;
  }, {});
};

/**
 * Get empty stats object
 */
const getEmptyStats = () => ({
  totalPlays: 0,
  totalCompletions: 0,
  totalShares: 0,
  totalCtas: 0,
  totalLeaderboardJoins: 0,
  avgDurationSeconds: 0,
  completionRate: 0,
  shareRate: 0,
  ctaRate: 0,
  devices: { mobile: 0, desktop: 0, tablet: 0 },
  lastUpdatedAt: null,
  source: 'empty'
});

/**
 * Clear stats cache for a game (memory + localStorage).
 * Call when campaign status changes (e.g. go live) so analytics refetch.
 */
export const invalidateGameStats = (gameId) => {
  memoryCache.delete(gameId);
  const dailyKeysToDelete = [...memoryCache.keys()].filter((k) => k.startsWith(`daily_${gameId}_`));
  dailyKeysToDelete.forEach((k) => memoryCache.delete(k));
  try {
    localStorage.removeItem(`${STATS_CACHE_KEY}_${gameId}`);
  } catch (e) {
    console.debug('Cache clear error:', e);
  }
};

/**
 * Clear all stats cache
 */
export const clearAllStatsCache = () => {
  memoryCache.clear();
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(STATS_CACHE_KEY) || key.startsWith(DASHBOARD_CHART_CACHE_KEY)) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.debug('Cache clear error:', e);
  }
};

// ============================================
// DASHBOARD CHART CACHE (Cloud Function results)
// ============================================

const DASHBOARD_CHART_CACHE_KEY = 'dashboard_chart_cache_v6';

/**
 * Get cached dashboard chart data.
 * Uses the same adaptive TTL curve as game stats.
 */
export const getCachedDashboardChart = (key) => {
  const fullKey = `${DASHBOARD_CHART_CACHE_KEY}_${key}`;
  const memoryCached = memoryCache.get(fullKey);
  if (memoryCached) {
    const totalPlays = memoryCached.data?.totals?.plays || 0;
    const cacheDuration = getStatsCacheDuration(totalPlays);
    if (cacheDuration > 0 && Date.now() - memoryCached.timestamp < cacheDuration) {
      return memoryCached.data;
    }
  }
  try {
    const cached = localStorage.getItem(fullKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      const totalPlays = parsed.data?.totals?.plays || 0;
      const cacheDuration = getStatsCacheDuration(totalPlays);
      if (cacheDuration > 0 && Date.now() - parsed.timestamp < cacheDuration) {
        memoryCache.set(fullKey, { data: parsed.data, timestamp: parsed.timestamp });
        return parsed.data;
      }
    }
  } catch (e) {
    console.debug('Dashboard chart cache read error:', e);
  }
  return null;
};

/**
 * Cache dashboard chart data.
 */
export const cacheDashboardChart = (key, data) => {
  const fullKey = `${DASHBOARD_CHART_CACHE_KEY}_${key}`;
  const now = Date.now();
  const payload = { ...data, timestamp: data?.timestamp ?? now };
  memoryCache.set(fullKey, { data: payload, timestamp: now });
  try {
    localStorage.setItem(fullKey, JSON.stringify({ data: payload, timestamp: now }));
  } catch (e) {
    console.debug('Dashboard chart cache write error:', e);
  }
};

/**
 * Clear dashboard chart cache entries.
 */
export const clearDashboardChartCache = () => {
  const keysToDelete = [...memoryCache.keys()].filter(k => k.startsWith(DASHBOARD_CHART_CACHE_KEY));
  keysToDelete.forEach(k => memoryCache.delete(k));
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(DASHBOARD_CHART_CACHE_KEY)) localStorage.removeItem(key);
    });
  } catch (e) {
    console.debug('Dashboard chart cache clear error:', e);
  }
};
